/**
 * UpgradeModal
 *
 * Single-instance paywall modal mounted once at App.tsx root inside
 * <SubscriptionProvider>. Reads modalAction + dismissModal from
 * useSubscriptionGuard. When a free user clicks any paid affordance, the
 * action handler calls `requirePaid('Email Coach Smith at Michigan', ...)`,
 * which sets modalAction and surfaces this modal.
 *
 * Click "View membership" → navigates to /subscription-required.
 * Click backdrop or X → dismissModal().
 *
 * Visual style mirrors SubscriptionWarningBanner / SubscriptionRequired:
 * Manrope, #F9550D primary, #151133 dark, white card on dim backdrop.
 *
 * Spec: /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md §8 Phase 1 B2.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock } from 'react-feather';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';

// P2.3: stash key the SubscriptionRequired page reads to attribute the
// resulting Stripe checkout back to the paywall trigger.
export const TRIGGER_ACTION_STORAGE_KEY = 'paywall_trigger_action';

/**
 * Per-feature copy. Triggered by `requirePaid(<key>, …)`. Each entry should
 * explain WHAT THE FEATURE IS before the upsell — without that context the
 * modal reads like a generic ad. Keys match the labels passed into
 * requirePaid() across the portal (Recruiting Calendar, Camps, Share Profile,
 * Email Templates, Bulk Messaging, etc.). Actions that already describe
 * themselves (e.g. "Email Coach Smith at Michigan") fall through to the
 * generic copy below.
 */
const FEATURE_COPY: Record<string, { tagline: string; description: string; bullets: string[] }> = {
  'Recruiting Calendar': {
    tagline: 'Plan every outreach moment, end to end.',
    description:
      'A week-by-week calendar that tells you who to contact, when to follow up, and which evaluation windows are coming up. Built around NCAA quiet/contact periods so you never miss a window.',
    bullets: [
      'NCAA quiet, contact, and dead periods auto-marked by division',
      'Personalized follow-up reminders tied to each coach you message',
      'Camp + showcase dates from your saved schools',
      'Weekly action list so you always know what to do next',
    ],
  },
  'Camps': {
    tagline: 'Get in front of coaches in person.',
    description:
      'A searchable directory of college camps and showcases, filtered by your sport, position, and saved schools. See which staff will be there before you commit.',
    bullets: [
      'Filter by school, division, region, and date',
      'See coaching staff attending each camp',
      'Direct registration links — no chasing PDFs',
      'Track which camps your matched schools host',
    ],
  },
  'Share Profile': {
    tagline: 'A clean public profile coaches can actually use.',
    description:
      'Generate a shareable link that puts your video, stats, academics, and contact info on one page coaches can open from their phone — no app required on their end.',
    bullets: [
      'One link covers video, stats, GPA, and team context',
      'Track when coaches view your profile',
      'Custom URL you can drop in every cold email',
      'Auto-updates when you edit your profile',
    ],
  },
  'Email Templates': {
    tagline: 'Stop rewriting the same intro every time.',
    description:
      'Save your best intro emails as templates and personalize them per coach in seconds. Pulls your profile data into the right fields so each note still reads like you wrote it for them.',
    bullets: [
      'Personalize {{coach_name}}, {{school}}, {{position_need}} automatically',
      'Save multiple variants — intro, follow-up, post-camp',
      'Track replies per template so you know which ones work',
      'Pre-built starter templates if you don\'t know where to begin',
    ],
  },
  'Bulk Messaging': {
    tagline: 'The same coach outreach — to your whole list at once.',
    description:
      'Contacting coaches one at a time is free to start (your first few are on the house). Bulk Messaging is that same outreach at scale: pick a school list, pick a template, and send personalized emails to every coach in one pass — each still pulls in their name and school. It’s the fast lane for the same thing you can already do one coach at a time.',
    bullets: [
      'Everything single-coach contact does — just to your whole list in one pass',
      'Personalization tokens applied per recipient (name, school, position need)',
      'Reply tracking surfaces interested coaches first',
      'Pause / resume / retry without losing your place',
    ],
  },
  'Analytics': {
    tagline: 'See what\'s working and what\'s not.',
    description:
      'Track every coach you reach out to: who opened, who replied, which templates are landing, and which schools have gone cold. Stop guessing where your recruiting energy is going.',
    bullets: [
      'Open + reply rate per coach and per school',
      'Template performance — see which intros actually convert',
      'Time-to-first-response, so you know when to follow up',
      'Stalled-school alerts when a coach goes quiet',
    ],
  },
  'Family Invites': {
    tagline: 'Get the whole family on the same page.',
    description:
      'Free accounts can add one family member; upgrading lets you add up to five. Bring in both parents, siblings, or the athlete themselves so everyone sees coach replies, calendar dates, and progress in one shared view.',
    bullets: [
      'Up to 5 total members on the family account',
      'Multiple guardians share the recruiting workload',
      'Multiple athletes — one account for siblings',
      'Each member gets their own login and notifications',
    ],
  },
};

// One plan (Premium retired 2026-09-11): copy speaks of the APS Membership,
// never of "plans" to choose between.
const GENERIC_COPY = {
  tagline: 'Unlock outreach with an APS Membership',
  description:
    'A subscription unlocks coach contact info, outbound email and DM, real recruiting analytics, and the integrations to send from your own accounts.',
  bullets: [
    'Coach names, emails, phones, social handles — fully visible',
    'Email / DM / Tweet coaches from your account',
    'Connect Gmail, X, YouTube for tracked outreach',
    'Real analytics with reply tracking',
  ],
};

const UpgradeModal: React.FC = () => {
  const { isPaid, modalAction, dismissModal } = useSubscriptionGuard();
  const navigate = useNavigate();

  // Bug G: auto-dismiss when subscription state flips to paid while the
  // modal is open. P2.7 made `isPaid` live (socket subscription:updated),
  // but without this effect the modal stayed on screen until the user
  // manually closed it — even though every paid affordance was already
  // unlocked behind it.
  useEffect(() => {
    if (isPaid && modalAction) dismissModal();
  }, [isPaid, modalAction, dismissModal]);

  if (!modalAction) return null;

  // modalAction is a known FEATURE_COPY key (a noun like "Analytics") for
  // feature gates, but a raw verb-phrase label (e.g. "Contact Coach Smith")
  // for ad-hoc gates. Only the former reads correctly after "Upgrade to".
  const knownFeature = !!FEATURE_COPY[modalAction];
  const copy = FEATURE_COPY[modalAction] || GENERIC_COPY;

  const handleUpgrade = () => {
    // Park the trigger label so SubscriptionRequired → Stripe checkout can
    // pass it into stripe_session.metadata. Webhook then fires
    // paywall_converted with the same trigger_action. dismissModal() below
    // also fires paywall_dismissed — both are accurate ("dismissed via
    // upgrade click" vs "dismissed via X/backdrop"). PostHog distinguishes
    // them by whether a paywall_converted lands shortly after.
    try {
      sessionStorage.setItem(TRIGGER_ACTION_STORAGE_KEY, modalAction);
    } catch {}
    dismissModal();
    navigate('/subscription-required');
  };

  return (
    <div
      onClick={dismissModal}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(21, 17, 51, 0.7)',
        // Sits above AccountSettingsModal sub-layers (10001/10002) and the
        // notification dropdown / fixed banners. Bump again if any new
        // global overlay outranks this.
        zIndex: 10100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          fontFamily: 'Manrope, sans-serif',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: '#151133',
            padding: '24px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '22px',
              background: '#F9550D',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Lock size={22} color="white" strokeWidth={2.5} />
          </div>
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              {knownFeature ? `Upgrade to ${modalAction}` : 'Upgrade to unlock this'}
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {copy.tagline}
            </p>
          </div>
          <button
            onClick={dismissModal}
            aria-label="Close"
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              width: '32px',
              height: '32px',
              borderRadius: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color="white" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>
          <p
            style={{
              margin: '0 0 16px 0',
              fontSize: '15px',
              color: '#151133',
              lineHeight: 1.5,
            }}
          >
            {copy.description}
          </p>
          <ul
            style={{
              margin: '0 0 24px 0',
              paddingLeft: '20px',
              color: 'rgba(21, 17, 51, 0.75)',
              fontSize: '14px',
              lineHeight: 1.7,
            }}
          >
            {copy.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={dismissModal}
              style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 600,
                background: 'white',
                color: '#151133',
                border: '1px solid rgba(21, 17, 51, 0.2)',
                borderRadius: '10px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              Not now
            </button>
            <button
              onClick={handleUpgrade}
              style={{
                flex: 1.5,
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 700,
                background: '#F9550D',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif',
                boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
              }}
            >
              View membership
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
